import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { concatMap, delay, filter, mergeMap } from 'rxjs/operators';
import { EcommerceEventTypeV4 } from '../../common/analytics.js';
import { isEoeOrPunchout } from '../../common/constants/pathVariables.js';
import { getItemVariant } from '../../common/utils/analyticsUtils.js';
import { getManufacturer } from '../../common/utils/cartProductUtils.js';
import { findCatalogById } from '../../common/utils/catalogUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { ContactCreationType } from '../../components/ContactOrPurposeOfUse/ContactsOrPurposeOfUseUtils.js';
import { getItemIdentifier } from '../../components/DuplicateContactsCheckDialog/duplicateContactCheckDialogUtils.js';
import { CommercialProductType, ContactType } from '../../generated/api/models.js';
import {
  checkDuplicateContactsPrivateMethod,
  getDeliveryMethodsPrivateMethod,
} from '../../generated/api/uiApiMethods.js';
import { getSessionData } from '../../public/site/sessionDataStore.js';
import {
  TypeKeys,
  addCartUpdateEventToAnalytics,
  catalogToValidationRules,
  clearCartItems,
  duplicatContactsCheckFulfilled,
  duplicateContactsCheck,
  duplicateContactsCheckFailed,
  eppSolutionToValidationRules,
  googleEcommerceAddToCart,
  googleEcommerceRemoveFromCart,
  loadDeliveryMethodsFailed,
  loadDeliveryMethodsFulfilled,
  notifyShoppingCartChange,
  postProcessCartItems,
  processCartItems,
} from '../actions/index.js';
import {
  createEcommerceItemFromCommercialProducts,
  createEcommerceItemFromShoppingCartItem,
} from '../common/googleEcommerceEvent.js';
import { clearShoppingCart, getShoppingCart, setShoppingCart } from '../common/localStorageUtils.js';
import {
  addToShoppingCart,
  createCartItem,
  getCartItems,
  getCartItemsWithUpdatedPayment,
  updateCartItem,
} from '../common/shopping-cart/shoppingCartFunctions.js';
import { AnalyticsEventTypes } from '../common/shopping-cart/shoppingCartInterfaces.js';
import { ActionPhase } from '../common/storeUtils.js';
import { callGetUiApiWithRetry, callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState } from './epicUtils.js';
import type { EpicDependencies } from './epicUtils.js';
import type { ShoppingCartItemForCheckout } from '../../common/types/checkout.js';
import type { Contacts, ContactsDuplicateCheckRequest, DeliveryMethodsResponse } from '../../generated/api/models.js';
import type {
  ActionTypes,
  AddToCartAction,
  ClearCartItemsAction,
  DuplicateContactsCheckAction,
  LoadDeliveryMethodsAction,
  PostProcessCartItemsAction,
  ProcessAdditionalParametersInCartItemsAction,
  ProcessCartItemsAction,
  UpdateCartItemAction,
  UpdateCartItemPaymentAction,
} from '../actions/index.js';
import type { CartItemType, CartUpdateAnalyticsData } from '../common/shopping-cart/shoppingCartInterfaces.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';

const setOrClearShoppingCart = (cartItem?: CartItemType[]) => {
  cartItem ? setShoppingCart(JSON.stringify(cartItem)) : clearShoppingCart();
};

const runValidationsRules = (state$: StateObservable<State>) => {
  return isEoeOrPunchout(window.location.pathname)
    ? catalogToValidationRules(
        findCatalogById(
          state$.value.selfservice?.virtualCatalogs?.items,
          state$.value.selfservice?.virtualCatalogs?.selectedCatalogCode,
          true
        )
      )
    : eppSolutionToValidationRules(getSessionData().companyInfo?.eppSolution);
};

const generateUniqueId = (existingItems: CartItemType[]): string => {
  const newId = crypto.randomUUID();
  // Probability is near zero for collision, but handle anyways..
  if (existingItems.find(existingItem => existingItem.id === newId)) {
    return generateUniqueId(existingItems);
  } else {
    return newId;
  }
};

const addToCartEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.ADD_TO_CART).pipe(
    mergeMap((addToCartAction: AddToCartAction) => {
      const {
        cartConfig,
        cartItemContainer: { isPublicSite },
      } = addToCartAction;

      const shoppingCart = JSON.parse(getShoppingCart());

      const uniqueId = generateUniqueId(shoppingCart);
      const cartItem = createCartItem(uniqueId, addToCartAction);

      addToShoppingCart(cartItem, shoppingCart);

      if (addToCartAction.onAddToCart) {
        addToCartAction.onAddToCart();
      }

      const { commercialProduct, offer, onlineModel, quantity } = addToCartAction.cartItemContainer;

      let commercialProducts = [commercialProduct];
      if (commercialProduct.productType === CommercialProductType.SALES_PRODUCT) {
        // Subs have the price spread out between two commercialProducts in the same offer...
        commercialProducts = [
          commercialProduct,
          // Keep the 'original' CP as the first one in the array
          ...offer.commercialProducts.filter(
            cp => cp.commercialProductCode !== commercialProduct.commercialProductCode
          ),
        ];
      }
      const ecommerceItem = createEcommerceItemFromCommercialProducts(
        onlineModel.onlineModelCode,
        onlineModel.category,
        onlineModel.onlineModelName,
        getItemVariant(EcommerceEventTypeV4.ADD_TO_CART, commercialProduct.commercialProductName) ??
          commercialProduct.commercialProductName,
        commercialProducts,
        quantity,
        getManufacturer(onlineModel, commercialProduct.commercialProductName)
      );

      return of(
        processCartItems(
          true,
          !isPublicSite,
          JSON.stringify(shoppingCart),
          runValidationsRules(state$),
          cartConfig,
          JSON.stringify(cartItem),
          Date.now()
        ),
        notifyShoppingCartChange(),
        googleEcommerceAddToCart([ecommerceItem])
      ).pipe(delay(1));
    })
  );

const updateProductQuantityEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.UPDATE_CART_ITEM).pipe(
    mergeMap((action: UpdateCartItemAction) => {
      const cartItems: CartItemType[] = updateCartItem(action);
      setOrClearShoppingCart(cartItems);
      if (action.onUpdateCart) {
        action.onUpdateCart();
      }

      const delta = action.newQuantity - action.cartItem.quantity;
      const ecommerceItem = createEcommerceItemFromShoppingCartItem(action.cartItem, Math.abs(delta));

      const isEmployee = isEoeOrPunchout(window.location.pathname);
      return of(
        processCartItems(false, isEmployee, getShoppingCart(), runValidationsRules(state$)),
        notifyShoppingCartChange(),
        delta > 0 ? googleEcommerceAddToCart([ecommerceItem]) : googleEcommerceRemoveFromCart([ecommerceItem])
      );
    })
  );

const updateProductPaymentEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.UPDATE_CART_ITEM_PAYMENT).pipe(
    mergeMap((action: UpdateCartItemPaymentAction) => {
      const cartItems: CartItemType[] = getCartItemsWithUpdatedPayment(getCartItems(), action);
      setOrClearShoppingCart(cartItems);
      const isEmployee = isEoeOrPunchout(window.location.pathname);
      return of(
        processCartItems(false, isEmployee, getShoppingCart(), runValidationsRules(state$)),
        notifyShoppingCartChange()
      );
    })
  );

const getNewContactsFromCartItemsForDuplicateCheck = (
  cartItems: Array<ShoppingCartItemForCheckout> = []
): Contacts[] => {
  return (
    cartItems.flatMap((item, itemIndex) => {
      return item.purposeOfUseOrContacts
        .map((puc, pucIndex) => ({
          // adding filter after map as to maintain the indexing
          identifier: getItemIdentifier(itemIndex, pucIndex),
          purposeOfUseOrContact: puc,
        }))
        .filter(
          pucWithIdentifier =>
            pucWithIdentifier.purposeOfUseOrContact.contactId === ContactCreationType.CREATE_NEW_CONTACT &&
            pucWithIdentifier.purposeOfUseOrContact.newContact !== undefined
        )
        .map(pucWithIdentifier => ({
          identifier: pucWithIdentifier.identifier,
          contact: {
            contactType: ContactType.PERSON,
            person: pucWithIdentifier.purposeOfUseOrContact.newContact,
          },
        }));
    }) || []
  );
};

export const onProcessAdditionalParametersInCartItemsFulfilledEpic: Epic<
  ActionTypes,
  Action,
  State,
  EpicDependencies
> = (action$: ActionsObservable<ActionTypes>, state$: StateObservable<State>) =>
  action$.pipe(
    ofType(TypeKeys.PROCESS_ADDITIONAL_PARAMETER_IN_CART_ITEMS),
    filter(
      (action: ProcessAdditionalParametersInCartItemsAction) =>
        !action.validateOnly &&
        actionToActionState(action, state$, 'deviceCheckout').state!.phase === ActionPhase.SUCCESS
    ),
    mergeMap(() => [
      // NOTE! Loading companyInfo and billing accounts doesn't seem to belong here. Processing "additional parameters"
      //       doesn't seem to need them. They are however required to be loaded for later steps during checkout. The
      //       checkout should be doing the loading itself (via route loaders / session data), not relying on an
      //       unrelated redux action to magically load them.
      duplicateContactsCheck(),
    ])
  );

const checkForDuplicateNewContactInCartItemsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.DUPLICATE_CONTACTS_CHECK)), (action: DuplicateContactsCheckAction) =>
    actionToActionState(action, state$, 'deviceCheckout')
  ).pipe(
    concatMap(() => {
      const newContacts = getNewContactsFromCartItemsForDuplicateCheck(state$.value.deviceCheckout?.cartItems);
      if (newContacts.length < 1) {
        return of(duplicatContactsCheckFulfilled());
      }
      const contactsDuplicateCheckRequest: ContactsDuplicateCheckRequest = {
        contacts: newContacts,
      };
      return callUiApi({
        epicDependencies,
        failureAction: duplicateContactsCheckFailed,
        method: checkDuplicateContactsPrivateMethod(),
        payload: contactsDuplicateCheckRequest,
        state$,
        successAction: duplicatContactsCheckFulfilled,
      });
    })
  );

const clearCartItemsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.ofType(TypeKeys.CLEAR_CART_ITEMS).pipe(
    mergeMap((action: ClearCartItemsAction) => {
      setOrClearShoppingCart();
      if (action.forceReload) {
        if (isInBrowser() && window.location) {
          window.location.reload();
        }
      }
      return EMPTY;
    })
  );

const getCartItemsFromActionOrLocalStorage = (cartItemJsonFromAction?: string): CartItemType[] => {
  const cartItemJson = cartItemJsonFromAction || getShoppingCart();
  return JSON.parse(cartItemJson);
};

interface CartItemsSimplified {
  guid: string;
  quantity: number;
  productName: string;
}

const getMatchingCurrentCartItem = (
  currentCartItems: CartItemsSimplified[],
  previousCartItem: ShoppingCartItemForCheckout
): CartItemsSimplified => {
  const matchedCartItem = currentCartItems.filter(
    item => item.guid === previousCartItem.offerCode && item.productName === previousCartItem.productName
  );
  return matchedCartItem.length > 0
    ? matchedCartItem[0]
    : {
        guid: previousCartItem.offerCode,
        quantity: 0,
        productName: previousCartItem.productName,
      };
};

const getCartUpdateEventData = (
  currentCartItems: CartItemType[],
  previousCartItems?: Array<ShoppingCartItemForCheckout>
): CartUpdateAnalyticsData[] => {
  const productList = 'tuotesivu';
  const interaction = 'interaction';
  const currentCartItemsSimplified: CartItemsSimplified[] = currentCartItems.map(item => ({
    guid: item.bundleItems[0].product.guid,
    quantity: item.quantity,
    productName: item.bundleItems[0].product.name,
  }));
  const cartUpdateEventData: CartUpdateAnalyticsData[] = [];
  if (previousCartItems) {
    previousCartItems.map(previousCartItem => {
      const matchingCurrentCartItem = getMatchingCurrentCartItem(currentCartItemsSimplified, previousCartItem);
      const index = currentCartItemsSimplified.indexOf(matchingCurrentCartItem);
      if (index !== -1) {
        currentCartItemsSimplified.splice(index, 1);
      }
      const deltaQuantity = matchingCurrentCartItem.quantity - previousCartItem.quantity;
      if (deltaQuantity !== 0) {
        cartUpdateEventData.push({
          onlineModelCode: matchingCurrentCartItem.guid,
          deltaQuantity,
          analyticsEventType:
            deltaQuantity > 0 ? AnalyticsEventTypes.ADD_TO_CART : AnalyticsEventTypes.REMOVE_FROM_CART,
          event: interaction,
          target: productList,
        });
      }
    });
  }
  if (currentCartItemsSimplified.length > 0) {
    currentCartItemsSimplified.map(item =>
      cartUpdateEventData.push({
        onlineModelCode: item.guid,
        deltaQuantity: item.quantity,
        analyticsEventType: AnalyticsEventTypes.ADD_TO_CART,
        event: interaction,
        target: productList,
      })
    );
  }
  return cartUpdateEventData;
};

const processCartItemsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.PROCESS_CART_ITEMS).pipe(
    mergeMap((action: ProcessCartItemsAction) => {
      const cartUpdateEventData = getCartUpdateEventData(
        getCartItemsFromActionOrLocalStorage(action.cartItemJson),
        state$.value.deviceCheckout?.cartItems
      );

      return of(
        addCartUpdateEventToAnalytics(cartUpdateEventData),
        postProcessCartItems(
          action.cartUpdated,
          action.isEmployee,
          action.cartItemJson,
          action.validationRules,
          action.cartConfig,
          action.latestItemAddedToCart,
          action.latestItemAddedToCartTimestamp
        )
      );
    })
  );

const postProcessCartItemsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.POST_PROCESS_CART_ITEMS).pipe(
    filter((action: PostProcessCartItemsAction) => !action.updateStateOnly),
    mergeMap((action: PostProcessCartItemsAction) => {
      const originalCartItems = getCartItemsFromActionOrLocalStorage(action.cartItemJson);

      // Filter out old cartItems without additionalFields
      const cartItems: CartItemType[] = originalCartItems?.filter(cartItem =>
        cartItem.bundleItems.every(bundleItem => bundleItem.additionalFields !== undefined)
      );

      cartItems.forEach(cartItem => {
        if (cartItem.id === undefined) {
          cartItem.id = generateUniqueId(cartItems);
        }
      });

      setOrClearShoppingCart(cartItems);
      if (state$.value.deviceCheckout?.cartItems.length !== cartItems.length) {
        return [clearCartItems(true)];
      }
      return EMPTY;
    })
  );

export const loadDeliveryMethodEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  action$.ofType(TypeKeys.GET_DELIVERY_METHODS).pipe(
    concatMap((action: LoadDeliveryMethodsAction) => {
      const productType = action.productType && action.productType.length > 0 ? action.productType : undefined;
      return callGetUiApiWithRetry(
        {
          epicDependencies,
          state$,
          method: getDeliveryMethodsPrivateMethod({
            productType,
          }),
          successAction: (res: DeliveryMethodsResponse) => loadDeliveryMethodsFulfilled(res),
          failureAction: loadDeliveryMethodsFailed,
        },
        undefined,
        2000,
        action$,
        [500]
      );
    })
  );

export const deviceCheckoutEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  addToCartEpic,
  updateProductPaymentEpic,
  updateProductQuantityEpic,
  onProcessAdditionalParametersInCartItemsFulfilledEpic,
  clearCartItemsEpic,
  processCartItemsEpic,
  postProcessCartItemsEpic,
  checkForDuplicateNewContactInCartItemsEpic,
  loadDeliveryMethodEpic
);
